import { getPageData } from '@/utils/getPageData';
import { useQueryClient } from '@tanstack/react-query';
import {
  ExItem,
  GetExItemsResponse,
  useGetExItemsHook,
  useGetExItemsSuspenseInfinite,
} from 'ap-openapi';
import { AxiosResponse } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useGetExItemsAllSuspense = () => {
  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetExItemsSuspenseInfinite(
      {
        per: 1000,
      },
      {
        query: {
          getNextPageParam: (page) => {
            // FIXME: OpenAPIの間違い？
            if ('next' in page.data && page.data.next) {
              // @ts-ignore
              const url = new URL(page.data.next ?? '/', location.href);
              if (!url.searchParams.has('page')) return undefined;
              return Number(url.searchParams.get('page'));
            } else {
              if (page.data.pagination) {
                if (page.data.pagination.has_next_page)
                  return (page.data.pagination.page ?? 1) + 1;
              }
            }
            return undefined;
          },
        },
      }
    );
  if (error) throw error;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  return useMemo(() => getPageData(data, 'ex_items'), [data]);
};

/**
 * Custom hook that provides functionality to fetch expense items for multiple departments concurrently.
 *
 * This hook leverages React Query's infinite query functionality to fetch all expense items
 * for the specified department IDs in parallel using Promise.all. Each department's data
 * is fetched with pagination support (1000 items per page) and automatically handles
 * pagination to retrieve all available items.
 *
 * @returns {Object} Hook return object
 * @returns {Function} returns.getExItemsForDeptIds - Async function that accepts an array of department IDs
 *   and returns a Promise resolving to a Record<string, ExItem[]> where keys are department IDs
 *   and values are arrays of expense items for that department
 * @returns {boolean} returns.isLoading - Loading state indicating if any fetch operations are in progress
 *
 * @example
 * ```tsx
 * const { getExItemsForDeptIds, isLoading } = useGetExItemsForDeptIds();
 *
 * const handleFetch = async () => {
 *   const result = await getExItemsForDeptIds(['dept1', 'dept2', 'dept3']);
 *   // result = { 'dept1': [ExItem, ...], 'dept2': [ExItem, ...], 'dept3': [ExItem, ...] }
 * };
 * ```
 */
export const useGetExItemsForDeptIds = () => {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const getExItems = useGetExItemsHook();

  const fetchExItemsForDept = useCallback(
    async (deptId: string) => {
      try {
        const queryKey = ['ex_items', deptId];
        const exItemsData = await queryClient.fetchInfiniteQuery({
          queryKey,
          queryFn: async ({ pageParam = 1 }) => {
            return await getExItems({
              dept_id: deptId,
              per: 1000,
              page: pageParam,
            });
          },
          getNextPageParam: (page: AxiosResponse<GetExItemsResponse>) => {
            if ('next' in page.data && page.data.next) {
              // @ts-ignore
              const url = new URL(page.data.next ?? '/', location.href);
              if (!url.searchParams.has('page')) return undefined;
              return Number(url.searchParams.get('page'));
            } else if (page.data.pagination) {
              if (page.data.pagination.has_next_page)
                return (page.data.pagination.page ?? 1) + 1;
            }
            return undefined;
          },
          initialPageParam: 1,
        });

        return getPageData(exItemsData, 'ex_items');
      } catch {
        return [];
      }
    },
    [queryClient, getExItems]
  );

  const getExItemsForDeptIds = useCallback(
    async (deptIds: string[]): Promise<Record<string, ExItem[]>> => {
      if (deptIds.length === 0) return {};

      setIsLoading(true);

      try {
        const fetchPromises = deptIds.map(async (deptId) => ({
          deptId,
          items: await fetchExItemsForDept(deptId),
        }));

        const results = await Promise.all(fetchPromises);
        return Object.fromEntries(
          results.map(({ deptId, items }) => [deptId, items])
        );
      } catch {
        return {};
      } finally {
        setIsLoading(false);
      }
    },
    [fetchExItemsForDept]
  );

  return {
    getExItemsForDeptIds,
    isLoading,
  };
};
